import React, { useEffect, useState } from "react";
import { teacherStudent } from "../../../../Api/apiUtils";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { Table, Field, SearchInput, Loader, EmptyState } from "../../../../ui";
import { SegmentedControl } from "./ui-pending";
import "./studentData.scss";
import { formatDate } from "../../../../utils/formatDate";
import ClickToCall from "../../../../Telephony/ClickToCall";
import { isTeacherLeaderDisabled } from "../../../../utils/permissions";

interface Student {
  studentId: number;
  studentUserId: any;
  emailId: string;
  studentName: string;
  countryCode: string;
  mobileNumber: string;
  lastClassTaken: string;
  expiryDate: string;
  studentCredentialId: string;
  enrollmentDate: any;
  courseName: any;
  courseId?: any;
  communityId: any;
  ageGroup?: string;
  city?: string;
  completedClasses?: number;
  // gradedCertification?: string;
}

/*
const NON_GRADED_CERTIFICATES = new Set([
  "no certificate",
  "n/a",
  "none",
  "not sure!",
]);

const formatCertificateChoice = (cert?: string) => {
  if (!cert) return "-";
  const normalized = cert.trim().toLowerCase();
  if (NON_GRADED_CERTIFICATES.has(normalized)) {
    return `Non-graded (${cert})`;
  }
  return `Graded (${cert})`;
};
*/

interface StudentDataResponse {
  paid: { data: Student[]; count: number };
  engaged: { data: Student[]; count: number };
  active: { data: Student[]; count: number };
  disengaged: { data: Student[]; count: number };
  expired: { data: Student[]; count: number };
}

interface StudentListProps {
  credentialId: string;
  teacherId: any;
  activeTab: any;
}

const StudentData: React.FC<StudentListProps> = ({ credentialId, teacherId }) => {
  const [studentData, setStudentData] = useState<StudentDataResponse | null>(null);
  const [activeTab, setActiveTab] = useState<keyof StudentDataResponse>("paid"); // Changed default to "paid"
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const navigate = useNavigate(); // Hook for navigation
  const hideStudentContact = isTeacherLeaderDisabled();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const storedTeacherId = localStorage.getItem("teacherProfileUserId");
      try {
        const response = await teacherStudent(storedTeacherId);

        if (!response || Object.keys(response).length === 0) {
          setNoData(true);
          setStudentData(null);
        } else {
          setNoData(false);
          setStudentData(response);
        }
      } catch (error) {
        console.error("Error fetching student data:", error);
        setNoData(true);
      }
      setLoading(false);
    };

    fetchData();
  }, [teacherId]);

  // Handle click on student name
  const handleCardClick = (studentCredentialId: string, studentId: number) => {
    if (studentCredentialId && studentId) {
      navigate(`/pages/student-profile/${studentCredentialId}/${studentId}`); // Navigate to student profile page
    } else {
      console.error("Missing studentCredentialId or studentId");
    }
  };


  // Filter students based on search query
  const filterStudents = (students: Student[]) => {
    if (!searchQuery) return students; // Return all students if no search query

    const lowerCaseQuery = searchQuery.toLowerCase();
    return students.filter(
      (student) =>
        student.studentName.toLowerCase().includes(lowerCaseQuery) ||
        student.emailId.toLowerCase().includes(lowerCaseQuery) ||
        student.mobileNumber.toLowerCase().includes(lowerCaseQuery) ||
        student.courseName.toLowerCase().includes(lowerCaseQuery) ||
        (student.ageGroup || "").toLowerCase().includes(lowerCaseQuery) ||
        (student.city || "").toLowerCase().includes(lowerCaseQuery)
        // (student.gradedCertification || "").toLowerCase().includes(lowerCaseQuery)
    );
  };

  const tabOrder: (keyof StudentDataResponse)[] = ["paid", "engaged", "active", "disengaged", "expired"];

  // Define tab descriptions
  const tabDescriptions = {
    paid: "Total Paid Student",
    engaged: "Learners who have taken a class in the last 30 days",
    active: "All learners who can take classes with you and course has not expired",
    disengaged: "Learners who have not taken a class in the last 30 days",
    expired: "Learners whose course has expired or have no pending classes left"
  };


  return (
    <div className="tp-students">
      {loading ? (
        <Loader label="Loading students…" />
      ) : noData ? (
        <EmptyState title="No data found" description="This teacher has no students yet." />
      ) : (
        <>
          {/* Search Bar */}
          <Field label="Search students" className="tp-students__search">
            {(control) => (
              <SearchInput
                {...control}
                placeholder="Search by name, email,phone number or course name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            )}
          </Field>

          {/* Fixed Navigation Tabs */}
          <div className="tp-students__tabs">
            <SegmentedControl
              label="Student segment"
              value={activeTab}
              onChange={(v) => setActiveTab(v as keyof StudentDataResponse)}
              options={tabOrder.map((category) => ({
                value: category,
                label: `${category.charAt(0).toUpperCase() + category.slice(1)} (${studentData?.[category]?.count || 0})`,
              }))}
            />
          </div>

          {tabOrder
            .filter((category) => category === activeTab)
            .map((category) => (
              <div key={category} className="tp-students__pane">
                <p className="tp-students__desc ds-text-muted">{tabDescriptions[category]}</p>
                <div className="tp-students__scroll">
                  <Table caption={`${category} students`} className="tp-students__table">
                    <thead>
                      <tr>
                        <th scope="col">Student ID</th>
                        <th scope="col">Student Name</th>
                        <th scope="col">Course Name</th>
                        <th scope="col">Age Group</th>
                        <th scope="col">City</th>
                        <th scope="col">Classes Completed</th>
                        {/* <th>Graded/Non-graded</th> */}
                        <th scope="col">Email</th>
                        <th scope="col">Mobile Number</th>
                        <th scope="col">Last Class Taken</th>
                        <th scope="col">Enrollment Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentData?.[category]?.data?.length ? (
                        filterStudents(studentData[category].data).map((student) => (
                          <tr key={student.studentId}>
                            <td className="ds-num">{student.studentId}</td>
                            <td>
                              <button
                                type="button"
                                className="tp-students__link"
                                onClick={() => handleCardClick(student.studentCredentialId, student.studentId)}
                              >
                                {student.studentName}
                              </button>
                            </td>

                            <td>{student.courseName}</td>
                            <td>{student.ageGroup || "-"}</td>
                            <td>{student.city || "-"}</td>
                            <td className="ds-num">{student.completedClasses ?? "-"}</td>
                            {/* <td>{formatCertificateChoice(student.gradedCertification)}</td> */}
                            <td>
                              {hideStudentContact ? (
                                <span className="tp-students__masked" title="Hidden for Teacher Leaders">
                                  ••••@••••.com
                                </span>
                              ) : (
                                student.emailId
                              )}
                            </td>
                            <td>
                              {hideStudentContact ? (
                                <span className="tp-students__masked" title="Hidden for Teacher Leaders">
                                  ••••••••••••
                                </span>
                              ) : (
                                <ClickToCall
                                  number={
                                    student.mobileNumber
                                      ? `${student.countryCode || ""}${student.mobileNumber}`
                                      : undefined
                                  }
                                  name={student.studentName}
                                  sourceRecordId={String(student.studentId)}
                                  customerUserId={student.studentUserId != null ? String(student.studentUserId) : undefined}
                                />
                              )}
                            </td>
                            <td>{student.lastClassTaken}</td>
                            <td>{formatDate(student.enrollmentDate, "dateTime")}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={10}>
                            <EmptyState title="No student data available" />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            ))}
        </>
      )}
    </div>
  );
};

export default StudentData;